import type {
  MiceCandidate,
  MiceChosenFlight,
  MiceDirectionDecision,
  MiceFlightLeg,
  MiceOutbound,
  MiceLegDirection,
  MiceNextAction,
  MicePhase,
  MiceRecommendation,
  MiceTraveler,
  MiceYellowReason,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, ToneChip, type ToneChipTone } from '@web/ui/core';
import { ColorDot } from './color_dot';
import { formatFlights, formatMoney, formatShortDay } from './need_format';
import { NeedErrorNote } from './need_status_chip';

export const PHASE_TONE: Record<MicePhase, ToneChipTone> = {
  preparation: 'gray',
  agent: 'ai',
  approval: 'brand',
  buying: 'amber',
  issued: 'green',
  attention: 'red',
  manual: 'amber',
};

const LEG_KEY = {
  OUTWARD: 'mice.travelers.leg.outbound',
  RETURN: 'mice.travelers.leg.inbound',
  BOTH: 'mice.travelers.leg.both',
} as const satisfies Record<MiceLegDirection, string>;

const legLabel = (direction: MiceLegDirection): string => t(LEG_KEY[direction]);

const SECOND_LINE = 'mt-0.5 truncate text-micro text-muted-foreground';

/** Detalhe da jornada: o status de cada sentido, ou o da necessidade quando ainda não há pedido. */
function journeyDetail(traveler: MiceTraveler): string {
  if (traveler.orders.length > 0) {
    return traveler.orders
      .map(({ order, direction }) =>
        t(`mice.travelers.legStatus.${order.status}`, { leg: legLabel(direction) }),
      )
      .join(' · ');
  }
  return traveler.need ? t(`mice.needs.status.${traveler.need.status}`) : '';
}

export function TravelerCell({ traveler }: Readonly<{ traveler: MiceTraveler }>) {
  const origin =
    traveler.need?.outboundOrigins.join('/') ?? traveler.orders[0]?.order.outbound?.origin;
  // A cor é a decisão de "Para emitir": nas outras fases a recomendação já ficou para trás.
  const color =
    traveler.phase === 'approval' ? (traveler.need?.recommendation?.color ?? null) : null;
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <ColorDot color={color} />
      <span className="truncate font-medium text-foreground">
        {traveler.name ?? traveler.email}
      </span>
      {origin ? (
        <span className="shrink-0 text-micro text-muted-foreground">
          {'· '}
          <span className="font-mono">{origin}</span>
        </span>
      ) : null}
    </div>
  );
}

/** Erro do primeiro pedido que tem um, com o sentido na frente: o viajante tem um pedido por
 *  sentido, e "Voucher cancelado" sozinho não diz qual trecho conferir (#6007). */
function orderErrorNote(traveler: MiceTraveler): string | null {
  const failing = traveler.orders.find((o) => o.order.error);
  if (!failing?.order.error) return null;
  const { message } = failing.order.error;
  return traveler.orders.length > 1
    ? t('mice.travelers.legError', { leg: legLabel(failing.direction), message })
    : message;
}

export function JourneyCell({ traveler }: Readonly<{ traveler: MiceTraveler }>) {
  const orderError = orderErrorNote(traveler);
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <ToneChip tone={PHASE_TONE[traveler.phase]}>{t(`mice.phase.${traveler.phase}`)}</ToneChip>
      <span className="text-micro text-muted-foreground">{journeyDetail(traveler)}</span>
      {traveler.need ? <NeedErrorNote need={traveler.need} /> : null}
      {!traveler.need?.error && orderError ? (
        <span className="text-meta text-destructive">{orderError}</span>
      ) : null}
    </div>
  );
}

const flightName = (leg: Pick<MiceFlightLeg, 'carrier' | 'flightNumber'>): string =>
  `${leg.carrier} ${leg.flightNumber}`;

/** O voo recomendado do sentido, quando bate com o voo comprado (para mostrar os horários). */
function chosenFor(traveler: MiceTraveler, direction: 'OUTWARD' | 'RETURN') {
  const rec = traveler.need?.recommendation;
  return (direction === 'OUTWARD' ? rec?.outbound : rec?.inbound) ?? null;
}

function orderLeg(
  traveler: MiceTraveler,
  direction: 'OUTWARD' | 'RETURN',
): MiceOutbound | MiceFlightLeg | null {
  const exact = traveler.orders.find((o) => o.direction === direction)?.order.outbound;
  if (exact) return exact;
  const both = traveler.orders.find((o) => o.direction === 'BOTH')?.order;
  if (!both) return null;
  return direction === 'OUTWARD' ? both.outbound : both.inbound;
}

/** "AD 4570 · 09:55 26/11": o voo, a partida quando a recomendação é o mesmo voo, e o dia. */
function boughtLine(bought: MiceOutbound | MiceFlightLeg, chosen: MiceChosenFlight | null): string {
  const first = chosen?.hops[0];
  const same = first?.carrier === bought.carrier && first.flightNumber === bought.flightNumber;
  const departs = chosen && same ? `${chosen.departAt.slice(11, 16)} ` : '';
  return `${flightName(bought)} · ${departs}${formatShortDay(bought.date)}`;
}

const chosenLine = (chosen: MiceChosenFlight): string =>
  `${formatFlights(chosen)} · ${chosen.departAt.slice(11, 16)} ${formatShortDay(chosen.departAt.slice(0, 10))}`;

/** Ainda sem voo: a data pedida, ou "não precisa" quando a viagem não tem esse sentido. */
function pendingLine(traveler: MiceTraveler, direction: 'OUTWARD' | 'RETURN'): string {
  const need = traveler.need;
  const skipped = direction === 'OUTWARD' ? 'return_only' : 'outbound_only';
  if (need?.tripKind === skipped) return `— · ${t('mice.travelers.notNeeded')}`;
  const date = direction === 'OUTWARD' ? need?.outboundDate : need?.returnDate;
  return date ? `— · ${formatShortDay(date)}` : '—';
}

/** "LA 3410 15:30": o voo (com as conexões) e a partida. */
const flightAt = (flight: MiceCandidate): string =>
  `${formatFlights(flight)} ${flight.departAt.slice(11, 16)}`;

interface LegSummary {
  /** O voo pedido pelo viajante; `null` quando não pediu ou não foi encontrado. */
  requested: string | null;
  chosen: string | null;
  /** O agente manteve o pedido. */
  kept: boolean;
  /** O primeiro amarelo do sentido. */
  reason: MiceYellowReason | null;
}

/** O mesmo voo: os mesmos trechos (companhia, número e partida). Os ids vêm de buscas diferentes. */
const sameFlight = (a: MiceCandidate, b: MiceCandidate): boolean =>
  a.hops.length === b.hops.length &&
  a.hops.every((h, i) => {
    const o = b.hops[i];
    return (
      o?.carrier === h.carrier && h.flightNumber === o.flightNumber && h.departAt === o.departAt
    );
  });

/**
 * Pedido × escolhido de um sentido, como a grid os mostra; sem decisão (recomendação antiga), nada.
 * O operador que escolhe de volta o voo pedido (`operator_choice`) também mantém o pedido.
 */
function legSummary(
  decision: MiceDirectionDecision | undefined,
  rec: MiceRecommendation,
): LegSummary {
  if (!decision) return { requested: null, chosen: null, kept: false, reason: null };
  const chosen = decision.direction === 'OUTWARD' ? rec.outbound : rec.inbound;
  return {
    requested: decision.requested ? flightAt(decision.requested) : null,
    chosen: chosen ? flightAt(chosen) : null,
    kept:
      decision.decision === 'kept' ||
      (decision.requested !== null && chosen !== null && sameFlight(decision.requested, chosen)),
    reason: decision.yellow[0] ?? null,
  };
}

/** A decisão do agente no sentido: o pedido riscado → o escolhido (ou "pedido mantido") e o amarelo. */
function DecisionLine({
  summary,
  decision,
}: Readonly<{ summary: LegSummary; decision: MiceDirectionDecision }>) {
  const swapped = !summary.kept && summary.requested !== null;
  const savings =
    swapped && decision.savings && decision.savings.amount > 0 ? decision.savings : null;
  return (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <div className="whitespace-nowrap tabular-nums text-foreground">
        {swapped ? (
          <>
            <s className="text-muted-foreground">{summary.requested}</s>
            {' → '}
          </>
        ) : null}
        {summary.chosen}
      </div>
      <div className="flex flex-wrap gap-1">
        {summary.kept ? <ToneChip tone="gray">{t('mice.leg.kept')}</ToneChip> : null}
        {savings ? (
          <ToneChip tone="green">
            {t('mice.leg.savings', { amount: formatMoney(savings) })}
          </ToneChip>
        ) : null}
        {summary.reason ? (
          <ToneChip tone="amber">{t(`mice.yellow.${summary.reason}.short`)}</ToneChip>
        ) : null}
      </div>
    </div>
  );
}

function legLine(traveler: MiceTraveler, direction: 'OUTWARD' | 'RETURN'): string {
  const chosen = chosenFor(traveler, direction);
  return chosen ? chosenLine(chosen) : pendingLine(traveler, direction);
}

/** Comprado: o voo do pedido. Antes da compra: a decisão do agente no sentido, quando há. */
export function LegCell({
  traveler,
  direction,
}: Readonly<{ traveler: MiceTraveler; direction: 'OUTWARD' | 'RETURN' }>) {
  const bought = orderLeg(traveler, direction);
  const rec = traveler.need?.recommendation;
  const decision = rec?.directions.find((d) => d.direction === direction);
  const summary = rec && !bought ? legSummary(decision, rec) : null;
  if (decision && summary?.chosen) {
    return <DecisionLine summary={summary} decision={decision} />;
  }
  return (
    <div className="whitespace-nowrap tabular-nums text-foreground">
      {bought ? boughtLine(bought, chosenFor(traveler, direction)) : legLine(traveler, direction)}
    </div>
  );
}

function valueLines(traveler: MiceTraveler): [string, string] {
  const paid = traveler.orders.flatMap((o) => (o.order.pricePaid ? [o.order.pricePaid] : []));
  const currency = paid[0]?.currency;
  if (currency && paid.every((p) => p.currency === currency)) {
    const amount = paid.reduce((sum, p) => sum + p.amount, 0);
    const all = paid.length === traveler.orders.length;
    return [
      formatMoney({ amount, currency }),
      t(all ? 'mice.travelers.value.paid' : 'mice.travelers.value.partial'),
    ];
  }
  const total = traveler.need?.recommendation?.total;
  return total ? [formatMoney(total), t('mice.travelers.value.recommended')] : ['—', ''];
}

export function ValueCell({ traveler }: Readonly<{ traveler: MiceTraveler }>) {
  const [first, second] = valueLines(traveler);
  return (
    <div className="text-right">
      <div className="whitespace-nowrap tabular-nums text-foreground">{first}</div>
      <div className={SECOND_LINE}>{second}</div>
    </div>
  );
}

/** Ações que viram botão na linha; as demais (aguardando, emitido) são só texto. */
const ACTIONABLE: ReadonlySet<MiceNextAction> = new Set([
  'recommend',
  'approve',
  'review',
  'reconsider',
  'buy',
  'retry',
  'verify',
  'complete_profile',
  'details',
]);
const DANGER_ACTION: ReadonlySet<MiceNextAction> = new Set(['retry', 'verify', 'complete_profile']);

function actionVariant(action: MiceNextAction): 'primary' | 'danger' | 'ghost' | 'outline' {
  if (action === 'approve') return 'primary';
  if (DANGER_ACTION.has(action)) return 'danger';
  return action === 'details' ? 'ghost' : 'outline';
}

interface NextActionCellProps {
  traveler: MiceTraveler;
  /** Sem permissão de compra, a próxima ação aparece só como texto (exceto "ver detalhes"). */
  canAct: boolean;
  /** Ação existe mas está bloqueada agora (ex.: recomendar sem premissas conferidas). */
  blocked: (action: MiceNextAction) => boolean;
  onAction: (traveler: MiceTraveler) => void;
}

/** O texto da próxima ação: com o agente "Aguardando" vira "Agente trabalhando", e em Atenção o `details` é o motivo. */
function nextActionKey({
  phase,
  nextAction,
}: MiceTraveler): `mice.travelers.next.${MiceNextAction | 'agentWorking' | 'detailsReason'}` {
  if (phase === 'agent' && nextAction === 'wait') return 'mice.travelers.next.agentWorking';
  if (phase === 'attention' && nextAction === 'details') return 'mice.travelers.next.detailsReason';
  return `mice.travelers.next.${nextAction}`;
}

export function NextActionCell({
  traveler,
  canAct,
  blocked,
  onAction,
}: Readonly<NextActionCellProps>) {
  const action = traveler.nextAction;
  const label = t(nextActionKey(traveler));
  // "Ver motivo"/"Ver detalhes" só abrem o painel: ler não exige permissão de compra.
  const allowed = canAct || action === 'details';
  if (!allowed || !ACTIONABLE.has(action)) {
    return <span className="whitespace-nowrap text-meta text-muted-foreground">{label}</span>;
  }
  return (
    // Segura o clique do botão (do teclado também, que borbulha como clique) antes da linha, que
    // abre o painel: a ilha não é controle, sai da árvore de acessibilidade.
    <span
      role="presentation"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Button
        size="sm"
        variant={actionVariant(action)}
        disabled={blocked(action)}
        aria-label={t('mice.travelers.actionFor', {
          action: label,
          name: traveler.name ?? traveler.email,
        })}
        onClick={() => {
          onAction(traveler);
        }}
      >
        {label}
      </Button>
    </span>
  );
}
